import React from 'react';
import PropTypes from 'prop-types';
import {TextInput} from '@/components/atoms';
import styles from '../RewardSetup.module.scss';
import {useAppSelector} from '@/store';
import {getCurrencySymbol} from '@/utils/commons';
import {preventInvalidNumericInput} from '@/utils/inputValidations';
/**
 * DigitalCondition component displays the digital code reward condition UI
 * Handles digital code amount, partner/supplier, and redeem points inputs
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing digitalCodeAmount, digitalCodePartner, redeemPoints
 * @param {Function} props.handleDigitalCodeAmountChange - Handler for digital code amount
 * @param {Function} props.handleDigitalCodePartnerChange - Handler for digital code partner
 * @param {Function} props.handleRedeemPointsChange - Handler for redeem points
 * @param {string} props.digitalCodeAmountError - Error message for amount validation
 * @param {string} props.digitalCodePartnerError - Error message for partner validation
 * @param {string} props.redeemPointsError - Error message for points validation
 * @returns {JSX.Element} The rendered DigitalCondition component
 */
const DigitalCondition = ({
  formData,
  handleDigitalCodeAmountChange,
  handleDigitalCodePartnerChange,
  handleRedeemPointsChange,
  digitalCodeAmountError,
  digitalCodePartnerError,
  redeemPointsError
}) => {
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const currencySymbol = getCurrencySymbol(countryCode);
  return (
    <div className='flex flex-wrap gap-[13px]'>
      <span className='mt-2 whitespace-nowrap text-sm'>{currencySymbol}</span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.digitalCodeAmount}
          onChange={handleDigitalCodeAmountChange}
          label=''
          placeholderText='Value'
          handleKeyDown={preventInvalidNumericInput}
          error={digitalCodeAmountError}
          isDark={true}
          type='number'
          id='reward-cash-amount'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>from</span>
      <div className={`w-44 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.digitalCodePartner}
          onChange={handleDigitalCodePartnerChange}
          label=''
          placeholderText='Supplier'
          error={digitalCodePartnerError}
          isDark={true}
          type='text'
          id='digital-supplier'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>by redeeming</span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.redeemPoints}
          onChange={handleRedeemPointsChange}
          label=''
          placeholderText='Points'
          handleKeyDown={preventInvalidNumericInput}
          error={redeemPointsError}
          isDark={true}
          type='number'
          id='redeem-points'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>points.</span>
    </div>
  );
};

DigitalCondition.propTypes = {
  formData: PropTypes.shape({
    digitalCodeAmount: PropTypes.string,
    digitalCodePartner: PropTypes.string,
    redeemPoints: PropTypes.string
  }).isRequired,
  handleDigitalCodeAmountChange: PropTypes.func.isRequired,
  handleDigitalCodePartnerChange: PropTypes.func.isRequired,
  handleRedeemPointsChange: PropTypes.func.isRequired,
  digitalCodeAmountError: PropTypes.string,
  digitalCodePartnerError: PropTypes.string,
  redeemPointsError: PropTypes.string
};

export default DigitalCondition;
